/**
 * API Configuration
 *
 * Centralizes backend API URL configuration and CSRF token handling.
 * Uses environment variables with fallback to localhost for development.
 *
 * Usage:
 *   import { API_BASE_URL, fetchWithCsrf, getCsrfToken } from '../config/api';
 *   // For GET requests
 *   fetch(`${API_BASE_URL}/bff/subscription`, { credentials: 'include' });
 *   // For POST requests
 *   fetchWithCsrf(`${API_BASE_URL}/bff/mock-subscribe`, { method: 'POST', body: JSON.stringify(data) });
 */

// IMPORTANT: Vite uses import.meta.env (not process.env)
// React apps built with Create React App use process.env
// This supports both patterns
export const API_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) ||
  process.env.REACT_APP_AZURE_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  'http://localhost:3001';

/**
 * Build full API endpoint URL
 * @param {string} path - API path (e.g., '/bff/subscription')
 * @returns {string} Full URL
 */
export function buildApiUrl(path) {
  // Remove leading slash if present to avoid double slashes
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${API_BASE_URL}/${cleanPath}`;
}

/**
 * Common fetch options for API calls with credentials
 */
export const fetchWithCredentials = {
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
  },
};

// ============================================================================
// CSRF Token Management
// ============================================================================

let csrfToken = null;
let csrfTokenPromise = null;

/**
 * Fetch CSRF token from backend
 * Caches the token and only fetches once per session
 *
 * @returns {Promise<string>} CSRF token
 */
export async function getCsrfToken() {
  // Return cached token if available
  if (csrfToken) {
    return csrfToken;
  }

  // Return existing promise if a fetch is in progress
  if (csrfTokenPromise) {
    return csrfTokenPromise;
  }

  // Fetch new token
  csrfTokenPromise = fetch(`${API_BASE_URL}/auth/csrf-token`, {
    method: 'GET',
    credentials: 'include',
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      csrfToken = data.csrfToken;
      csrfTokenPromise = null;
      console.log('[API] CSRF token acquired');
      return csrfToken;
    })
    .catch((error) => {
      csrfTokenPromise = null;
      console.error('[API] Failed to fetch CSRF token:', error);
      throw error;
    });

  return csrfTokenPromise;
}

/**
 * Clear cached CSRF token
 * Call this on logout or when token is invalidated
 */
export function clearCsrfToken() {
  csrfToken = null;
  csrfTokenPromise = null;
}

/**
 * Fetch wrapper that automatically includes CSRF token for POST/PUT/DELETE requests
 *
 * @param {string} url - Request URL
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export async function fetchWithCsrf(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();

  // Only add CSRF token for state-changing methods
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    const token = await getCsrfToken();

    options.headers = {
      ...options.headers,
      'X-CSRF-Token': token,
    };
  }

  // Always include credentials
  options.credentials = 'include';

  const response = await fetch(url, options);

  // Handle CSRF token errors - refetch token and retry once
  if (response.status === 403) {
    const errorData = await response.clone().json().catch(() => ({}));
    if (errorData.code === 'CSRF_INVALID') {
      console.log('[API] CSRF token invalid, refetching...');
      clearCsrfToken();
      const newToken = await getCsrfToken();

      options.headers = {
        ...options.headers,
        'X-CSRF-Token': newToken,
      };

      return fetch(url, options);
    }
  }

  // Handle token expiration - redirect to login
  if (response.status === 401) {
    const errorData = await response.clone().json().catch(() => ({}));
    if (errorData.code === 'TOKEN_EXPIRED') {
      console.log('[API] Token expired, redirecting to login...');
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `${API_BASE_URL}/auth/login?returnUrl=${returnUrl}`;
      return response;
    }
  }

  // Proactive token refresh: if server reports token is expiring soon,
  // trigger a background refresh so the user doesn't get interrupted
  const tokenExpiresIn = response.headers.get('X-Token-Expires-In');
  if (tokenExpiresIn) {
    const expiresIn = parseInt(tokenExpiresIn, 10);
    if (expiresIn > 0 && expiresIn < 300) { // Less than 5 minutes remaining
      console.log(`[API] Token expiring soon (${expiresIn}s), triggering background refresh`);
      fetch(`${API_BASE_URL}/bff/token-refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnUrl: window.location.pathname }),
      }).catch(() => {}); // Fire and forget
    }
  }

  return response;
}

export default API_BASE_URL;
